
// tried with 47 rallies - doesn't work well at all Feb 11, 2026 8:35am

import * as React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { KitchenArrivalRow } from "./RallyBoardPopup";

export type Team01 = 0 | 1;

export type RallyVideoPreviewSeg = {
  src: string;
  startSec: number;
  endSec: number;
  label: string;
  rallyIndex: number;
  servingTeam: Team01;
};

function buildPreviewSegmentUrl(args: {
  packageId: string;
  startMs: number;
  endMs: number;
  rallyIndex?: number | null;
  previewW: number;
  previewFps: number;
}) {
  const {
    packageId,
    startMs,
    endMs,
    rallyIndex,
    previewW, // = 320,
    previewFps, // = 12,
  } = args;

  const qs = new URLSearchParams();
  qs.set("packageId", packageId);
  qs.set("startMs", String(Math.round(startMs)));
  qs.set("endMs", String(Math.round(endMs)));
  if (Number.isFinite(rallyIndex as any))
    qs.set("rallyIndex", String(rallyIndex));
  qs.set("quality", "preview");
  qs.set("previewW", String(previewW));
  qs.set("previewFps", String(previewFps));

  return `/api/rally-segment-video?${qs.toString()}`;
}

function rowToSeg(
  row: KitchenArrivalRow,
  packageId: string,
): RallyVideoPreviewSeg | null {
  // we need timing + rally index
  const startMs = (row as any).startMs;
  const endMs = (row as any).endMs;
  const rallyIndex = (row as any).rallyIndex ?? 0;

  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs)
    return null;

  // ✅ preview segment URL
  const src = buildPreviewSegmentUrl({
    packageId,
    startMs,
    endMs,
    rallyIndex,
    previewW: 200, // 320,
    previewFps: 6, //12,
  });

  return {
    src,
    rallyIndex,
    startSec: startMs / 1000,
    endSec: endMs / 1000,
    label: `#${rallyIndex + 1}`,
    servingTeam: ((row as any).servingTeam ?? 0) as Team01,
  };
}

function useSafeLoopingVideoSegment(args: {
  enabled: boolean;
  startSec: number;
  endSec: number;
}) {
  const { enabled, startSec, endSec } = args;
  const ref = React.useRef<HTMLVideoElement | null>(null);

  React.useEffect(() => {
    const v = ref.current;
    if (!v) return;

    let raf = 0;
    let disposed = false;

    const clampStart = Math.max(0, startSec);
    const clampEnd = Math.max(clampStart + 0.05, endSec);

    const tryStart = async () => {
      if (!enabled || disposed) return;

      v.muted = true;

      if (Number.isNaN(v.duration) || v.readyState < 1) return;

      try {
        v.currentTime = clampStart;
      } catch {}

      try {
        await v.play();
      } catch {}

      const tick = () => {
        if (disposed || !enabled) return;
        const vv = ref.current;
        if (!vv) return;

        const t = vv.currentTime;
        if (Number.isFinite(t) && t >= clampEnd) {
          try {
            vv.currentTime = clampStart + 0.001;
          } catch {}
        }
        raf = requestAnimationFrame(tick);
      };

      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
    };

    const onLoadedMetadata = () => void tryStart();
    const onCanPlay = () => void tryStart();

    v.addEventListener("loadedmetadata", onLoadedMetadata);
    v.addEventListener("canplay", onCanPlay);
    void tryStart();

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      v.removeEventListener("loadedmetadata", onLoadedMetadata);
      v.removeEventListener("canplay", onCanPlay);
    };
  }, [enabled, startSec, endSec]);

  return ref;
}

function GridTile(props: { seg: RallyVideoPreviewSeg }) {
  const { seg } = props;

  const videoRef = useSafeLoopingVideoSegment({
    enabled: true,
    startSec: seg.startSec,
    endSec: seg.endSec,
  });

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        borderRadius: 1.25,
        overflow: "hidden",
        bgcolor: "rgba(0,0,0,0.35)",
        border: "1px solid rgba(255,255,255,0.14)",
      }}
    >
      <video
        ref={videoRef}
        src={seg.src}
        muted
        playsInline
        autoPlay
        loop
        preload="metadata"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          left: 6,
          top: 6,
          px: 0.75,
          py: 0.35,
          borderRadius: 1,
          fontSize: 12,
          fontWeight: 900,
          color: "rgba(255,255,255,0.95)",
          bgcolor: "rgba(0,0,0,0.62)",
          border: "1px solid rgba(255,255,255,0.16)",
          userSelect: "none",
          textShadow: "0 1px 1px rgba(0,0,0,0.35)",
        }}
      >
        {seg.label}
      </Box>
    </Box>
  );
}

function OverlayVideo(props: { seg: RallyVideoPreviewSeg; opacity: number }) {
  const { seg, opacity } = props;

  const videoRef = useSafeLoopingVideoSegment({
    enabled: true,
    startSec: seg.startSec,
    endSec: seg.endSec,
  });

  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      <video
        ref={videoRef}
        src={seg.src}
        muted
        autoPlay
        loop
        playsInline
        preload="metadata"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          opacity,
        }}
      />
    </Box>
  );
}

function OverlayAllVideos(props: {
  segs: RallyVideoPreviewSeg[];
  opacity: number;
  maxVideos: number;
}) {
  const { segs, opacity, maxVideos } = props;

  // ✅ don’t try to animate 47 stacked videos
  const renderSegs = React.useMemo(
    () => segs.slice(0, maxVideos),
    [segs, maxVideos],
  );

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: "rgba(0,0,0,0.55)",
        border: "2px solid rgba(255,255,255,0.22)",
      }}
    >
      {renderSegs.map((seg) => (
        <OverlayVideo
          key={`${seg.rallyIndex}-${seg.startSec}-${seg.endSec}`}
          seg={seg}
          opacity={opacity}
        />
      ))}

      <Box
        sx={{
          position: "absolute",
          left: 8,
          bottom: 8,
          display: "flex",
          flexWrap: "wrap",
          gap: 0.6,
          maxWidth: "calc(100% - 16px)",
          pointerEvents: "none",
          opacity: 0.95,
        }}
      >
        {renderSegs.map((s) => (
          <Box
            key={`tag-${s.rallyIndex}`}
            sx={{
              fontSize: 11,
              fontWeight: 900,
              px: 0.6,
              py: 0.2,
              borderRadius: 1,
              color: "rgba(255,255,255,0.92)",
              bgcolor: "rgba(0,0,0,0.45)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            {s.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function AllRallyVideosPopup_FromKitchenArrivalRows(props: {
  open: boolean;
  onClose: () => void;
  rows: KitchenArrivalRow[];
  packageId: string; // ✅ add this
}) {
  const { open, onClose, rows, packageId } = props;

  const segs = React.useMemo(() => {
    return rows
      .map((r) => rowToSeg(r, packageId))
      .filter((x): x is RallyVideoPreviewSeg => Boolean(x))
      .sort((a, b) => a.rallyIndex - b.rallyIndex);
  }, [rows, packageId]);

  const [mode, setMode] = React.useState<"grid" | "overlay">("grid");

  const OPACITY_CHOICES = [
    0.05, 0.08, 0.1, 0.12, 0.16, 0.2, 0.25, 0.33, 0.5,0.75, 0.9
  ] as const;
  const [overlayOpacity, setOverlayOpacity] =
    React.useState<(typeof OPACITY_CHOICES)[number]>(0.75);

  const OVERLAY_COUNT_CHOICES = [4, 8, 12, 16, 24] as const;
  const [overlayCount, setOverlayCount] = React.useState<number>(60);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "92vw",
          height: "86vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "rgba(15,15,18,0.98)",
          borderRadius: 2,
          border: "3px solid rgba(255,255,255,0.30)",
          resize: "both",
          overflow: "hidden",
          minWidth: 520,
          minHeight: 360,
        },
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(255,255,255,0.12)",
          color: "rgba(255,255,255,0.92)",
        }}
      >
        <Typography sx={{ fontWeight: 900, fontSize: 14, color: "inherit" }}>
          All rally videos ({segs.length})
        </Typography>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, v) => v && setMode(v)}
          sx={{
            ml: 1,
            "& .MuiToggleButton-root": {
              color: "rgba(255,255,255,0.85)",
              borderColor: "rgba(255,255,255,0.18)",
            },
            "& .MuiToggleButton-root.Mui-selected": {
              color: "rgba(0,0,0,0.92)",
              bgcolor: "rgba(255,255,255,0.88)",
            },
          }}
        >
          <ToggleButton value="grid">GRID</ToggleButton>
          <ToggleButton value="overlay">OVERLAY</ToggleButton>
        </ToggleButtonGroup>

        {mode === "overlay" && (
          <Box sx={{ ml: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 12, opacity: 0.85, color: "inherit" }}>
              Opacity
            </Typography>

            <FormControl size="small">
              <Select
                value={overlayOpacity}
                onChange={(e) =>
                  setOverlayOpacity(Number(e.target.value) as any)
                }
                sx={{
                  minWidth: 92,
                  fontSize: 12,
                  color: "rgba(255,255,255,0.92)",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.18)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.35)",
                  },
                  ".MuiSvgIcon-root": { color: "rgba(255,255,255,0.7)" },
                  ".MuiSelect-select": { py: 0.6 },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "rgba(20,20,24,0.98)",
                      color: "rgba(255,255,255,0.92)",
                    },
                  },
                }}
              >
                {OPACITY_CHOICES.map((v) => (
                  <MenuItem key={v} value={v} sx={{ fontSize: 12 }}>
                    {Math.round(v * 100)}%
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography
              sx={{ fontSize: 12, opacity: 0.85, color: "inherit", ml: 1 }}
            >
              Count
            </Typography>

            <FormControl size="small">
              <Select
                value={overlayCount}
                onChange={(e) => setOverlayCount(Number(e.target.value) as any)}
                sx={{
                  minWidth: 70,
                  fontSize: 12,
                  color: "rgba(255,255,255,0.92)",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.18)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.35)",
                  },
                  ".MuiSvgIcon-root": { color: "rgba(255,255,255,0.7)" },
                  ".MuiSelect-select": { py: 0.6 },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "rgba(20,20,24,0.98)",
                      color: "rgba(255,255,255,0.92)",
                    },
                  },
                }}
              >
                {OVERLAY_COUNT_CHOICES.map((v) => (
                  <MenuItem key={v} value={v} sx={{ fontSize: 12 }}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Box sx={{ flex: 1 }} />

        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: "rgba(255,255,255,0.85)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 0.75,
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {mode === "overlay" ? (
          <OverlayAllVideos
            segs={segs}
            opacity={overlayOpacity}
            maxVideos={overlayCount}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              width: "100%",
              display: "grid",
              gap: "2px",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              overflow: "auto",
              bgcolor: "rgba(0,0,0,0.55)",
              borderRadius: 1.5,
              border: "2px solid rgba(255,255,255,0.22)",
              p: 0.5,
              boxSizing: "border-box",
            }}
          >
            {segs.map((seg) => (
              <GridTile
                key={`${seg.rallyIndex}-${seg.startSec}-${seg.endSec}`}
                seg={seg}
              />
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// import * as React from "react";
// import {
//   Box,
//   Dialog,
//   DialogContent,
//   IconButton,
//   ToggleButton,
//   ToggleButtonGroup,
//   Typography,
//   FormControl,
//   Select,
//   MenuItem,
// } from "@mui/material";
// import CloseIcon from "@mui/icons-material/Close";

// import { KitchenArrivalRow } from "./RallyBoardPopup";
// import { useLoopingVideoSegment } from "./GameStory_ReasonsLostEtc";

// export type Team01 = 0 | 1;

// export type RallyVideoPreviewSeg = {
//   src: string;
//   startSec: number;
//   endSec: number;
//   label: string;
//   rallyIndex: number;
//   servingTeam: Team01;
// };

// function rowToSeg(row: KitchenArrivalRow): RallyVideoPreviewSeg | null {
//   if (!row.videoUrl) return null;

//   // IMPORTANT: row.startMs/endMs are ms
//   const startSec = row.startMs / 1000;
//   const endSec = row.endMs / 1000;
//   if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) return null;

//   const rallyIndex = row.rallyIndex ?? 0;

//   return {
//     src: row.videoUrl,
//     rallyIndex,
//     startSec,
//     endSec,
//     label: `#${rallyIndex + 1}`,
//     servingTeam: (row.servingTeam ?? 0) as Team01,
//   };
// }

// function useSize<T extends HTMLElement>() {
//   const ref = React.useRef<T | null>(null);
//   const [size, setSize] = React.useState({ w: 0, h: 0 });

//   React.useLayoutEffect(() => {
//     const el = ref.current;
//     if (!el) return;

//     const ro = new ResizeObserver((entries) => {
//       const cr = entries[0]?.contentRect;
//       if (!cr) return;
//       setSize({ w: cr.width, h: cr.height });
//     });

//     ro.observe(el);
//     return () => ro.disconnect();
//   }, []);

//   return { ref, size };
// }

// function computeBestGrid(args: { n: number; w: number; h: number; gapPx: number; aspect: number }) {
//   const { n, w, h, gapPx, aspect } = args;
//   if (!n || w <= 0 || h <= 0) return { cols: 1, rows: n };

//   let bestCols = 1;
//   let bestArea = 0;

//   for (let cols = 1; cols <= n; cols++) {
//     const rows = Math.ceil(n / cols);
//     const wAvail = w - gapPx * (cols - 1);
//     const hAvail = h - gapPx * (rows - 1);
//     if (wAvail <= 0 || hAvail <= 0) continue;

//     let tileW = wAvail / cols;
//     let tileH = tileW / aspect;
//     if (tileH * rows > hAvail) {
//       tileH = hAvail / rows;
//       tileW = tileH * aspect;
//     }

//     const area = tileW * tileH;
//     if (area > bestArea) {
//       bestArea = area;
//       bestCols = cols;
//     }
//   }

//   return { cols: bestCols, rows: Math.ceil(n / bestCols) };
// }

// function GridTile(props: { seg: RallyVideoPreviewSeg }) {
//   const { seg } = props;

//   const videoRef = useSafeLoopingVideoSegment({ //useLoopingVideoSegment({
//     enabled: true,
//     startSec: seg.startSec,
//     endSec: seg.endSec,
//   });

//   return (
//     <Box
//       sx={{
//         position: "relative",
//         width: "100%",
//         // height: "100%",
//         aspectRatio: "16 / 9",
//         borderRadius: 1.25,
//         overflow: "hidden",
//         bgcolor: "rgba(0,0,0,0.35)",
//         border: "1px solid rgba(255,255,255,0.14)",
//       }}
//     >
//       <video
//         ref={videoRef}
//         src={seg.src}
//         muted
//         playsInline
//         autoPlay
//         loop
//         preload="metadata"
//         style={{
//           width: "100%",
//           height: "100%",
//           objectFit: "cover",
//           display: "block",
//         }}
//       />

//       {/* ALWAYS ON label, high contrast */}
//       <Box
//         sx={{
//           position: "absolute",
//           left: 6,
//           top: 6,
//           px: 0.75,
//           py: 0.35,
//           borderRadius: 1,
//           fontSize: 12,
//           fontWeight: 900,
//           color: "rgba(255,255,255,0.95)",
//           bgcolor: "rgba(0,0,0,0.62)",
//           border: "1px solid rgba(255,255,255,0.16)",
//           userSelect: "none",
//           textShadow: "0 1px 1px rgba(0,0,0,0.35)",
//         }}
//       >
//         {seg.label}
//       </Box>
//     </Box>
//   );
// }

// function OverlayAllVideos(props: { segs: RallyVideoPreviewSeg[]; opacity: number }) {
//   const { segs, opacity } = props;

//   return (
//     <Box
//       sx={{
//         position: "relative",
//         width: "100%",
//         height: "100%",
//         borderRadius: 1.5,
//         overflow: "hidden",
//         bgcolor: "rgba(0,0,0,0.55)",
//         border: "2px solid rgba(255,255,255,0.22)",
//       }}
//     >
//       {segs.map((seg) => (
//         <OverlayVideo key={`${seg.rallyIndex}-${seg.startSec}-${seg.endSec}`} seg={seg} opacity={opacity} />
//       ))}

//       {/* Optional overlay label stack (always visible) */}
//       <Box
//         sx={{
//           position: "absolute",
//           left: 8,
//           bottom: 8,
//           display: "flex",
//           flexWrap: "wrap",
//           gap: 0.6,
//           maxWidth: "calc(100% - 16px)",
//           pointerEvents: "none",
//           opacity: 0.9,
//         }}
//       >
//         {segs.slice(0, 24).map((s) => (
//           <Box
//             key={`tag-${s.rallyIndex}`}
//             sx={{
//               fontSize: 11,
//               fontWeight: 900,
//               px: 0.6,
//               py: 0.2,
//               borderRadius: 1,
//               color: "rgba(255,255,255,0.92)",
//               bgcolor: "rgba(0,0,0,0.45)",
//               border: "1px solid rgba(255,255,255,0.12)",
//             }}
//           >
//             {s.label}
//           </Box>
//         ))}
//       </Box>
//     </Box>
//   );
// }

// function OverlayVideo(props: { seg: RallyVideoPreviewSeg; opacity: number }) {
//   const { seg, opacity } = props;

//   const videoRef = useSafeLoopingVideoSegment({
//     enabled: true,
//     startSec: seg.startSec,
//     endSec: seg.endSec,
//   });

//   return (
//     <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
//       <video
//         ref={videoRef}
//         src={seg.src}
//         muted
//         playsInline
//         autoPlay
//         loop
//         preload="metadata"
//         style={{
//           width: "100%",
//           height: "100%",
//           objectFit: "cover",
//           display: "block",
//           opacity,
//         }}
//       />
//     </Box>
//   );
// }

// function useSafeLoopingVideoSegment(args: {
//   enabled: boolean;
//   startSec: number;
//   endSec: number;
// }) {
//   const { enabled, startSec, endSec } = args;
//   const ref = React.useRef<HTMLVideoElement | null>(null);

//   React.useEffect(() => {
//     const v = ref.current;
//     if (!v) return;

//     let raf = 0;
//     let disposed = false;

//     const clampStart = Math.max(0, startSec);
//     const clampEnd = Math.max(clampStart + 0.05, endSec);

//     const tryStart = async () => {
//       if (!enabled || disposed) return;

//       // Always mute for autoplay safety
//       v.muted = true;

//       // Wait for metadata before seeking
//       if (Number.isNaN(v.duration) || v.readyState < 1) {
//         // will be called again via events
//         return;
//       }

//       // Seek into segment
//       try {
//         v.currentTime = clampStart;
//       } catch {
//         // ignore
//       }

//       // Try play (may still reject; ignore)
//       try {
//         await v.play();
//       } catch {
//         // ignore autoplay rejection
//       }

//       // Loop by checking currentTime
//       const tick = () => {
//         if (disposed || !enabled) return;
//         const vv = ref.current;
//         if (!vv) return;

//         const t = vv.currentTime;
//         if (Number.isFinite(t) && t >= clampEnd) {
//           // jump back near start; tiny epsilon avoids stuck frames
//           try {
//             vv.currentTime = clampStart + 0.001;
//           } catch {
//             // ignore
//           }
//         }
//         raf = requestAnimationFrame(tick);
//       };

//       cancelAnimationFrame(raf);
//       raf = requestAnimationFrame(tick);
//     };

//     const onLoadedMetadata = () => void tryStart();
//     const onCanPlay = () => void tryStart();

//     v.addEventListener("loadedmetadata", onLoadedMetadata);
//     v.addEventListener("canplay", onCanPlay);

//     // initial attempt (in case metadata already ready)
//     void tryStart();

//     return () => {
//       disposed = true;
//       cancelAnimationFrame(raf);
//       v.removeEventListener("loadedmetadata", onLoadedMetadata);
//       v.removeEventListener("canplay", onCanPlay);
//     };
//   }, [enabled, startSec, endSec]);

//   return ref;
// }
// export function AllRallyVideosPopup_FromKitchenArrivalRows(props: {
//   open: boolean;
//   onClose: () => void;
//   rows: KitchenArrivalRow[];
//   packageId: string;
// }) {
//   const { open, onClose, rows, packageId } = props;

//   const segs = React.useMemo(() => {
//     const r =  rows
//       .map((r) => rowToSeg(r))
//       .filter((x): x is RallyVideoPreviewSeg => Boolean(x))
//       .sort((a, b) => a.rallyIndex - b.rallyIndex);

//       console.log("AllRallyVideosPopup_FromKitchenArrivalRows: computed segs", r);
//     return r;
//   }, [rows]);

//   const [mode, setMode] = React.useState<"grid" | "overlay">("grid");

//   const OPACITY_CHOICES = [0.05, 0.08, 0.1, 0.12, 0.16, 0.2, 0.25, 0.33, 0.5] as const;
//   const [overlayOpacity, setOverlayOpacity] =
//     React.useState<(typeof OPACITY_CHOICES)[number]>(0.12);

//   const gapPx = 2;
//   const aspect = 16 / 9;

//   const { ref: hostRef, size } = useSize<HTMLDivElement>();

//   const grid = React.useMemo(() => {
//     return computeBestGrid({ n: segs.length, w: size.w, h: size.h, gapPx, aspect });
//   }, [segs.length, size.w, size.h]);

//   const canLayout = size.w > 10 && size.h > 10;

//   console.log({canLayout, grid, size});

//   return (
//     <Dialog
//       open={open}
//       onClose={onClose}
//       maxWidth={false}
//       PaperProps={{
//         sx: {
//           width: "92vw",
//           height: "86vh",
//           bgcolor: "rgba(15,15,18,0.98)",
//           borderRadius: 2,
//           border: "3px solid rgba(255,255,255,0.30)", // thicker
//           resize: "both",
//           overflow: "hidden",
//           minWidth: 520,
//           minHeight: 360,
//           flexDirection: "column",

//         },
//       }}
//     >
//       {/* Header */}
//       <Box
//         sx={{
//           px: 1.25,
//           py: 0.75,
//           display: "flex",
//           alignItems: "center",
//           gap: 1,
//           borderBottom: "1px solid rgba(255,255,255,0.12)",
//           color: "rgba(255,255,255,0.92)", // ✅ force visible
//         }}
//       >
//         <Typography sx={{ fontWeight: 900, fontSize: 14, color: "inherit" }}>
//           All rally videos ({segs.length})
//         </Typography>

//         <ToggleButtonGroup
//           size="small"
//           exclusive
//           value={mode}
//           onChange={(_, v) => v && setMode(v)}
//           sx={{
//             ml: 1,
//             "& .MuiToggleButton-root": {
//               color: "rgba(255,255,255,0.85)",
//               borderColor: "rgba(255,255,255,0.18)",
//             },
//             "& .MuiToggleButton-root.Mui-selected": {
//               color: "rgba(0,0,0,0.92)",
//               bgcolor: "rgba(255,255,255,0.88)",
//             },
//           }}
//         >
//           <ToggleButton value="grid">GRID</ToggleButton>
//           <ToggleButton value="overlay">OVERLAY</ToggleButton>
//         </ToggleButtonGroup>

//         {mode === "overlay" && (
//           <Box sx={{ ml: 2, display: "flex", alignItems: "center", gap: 1 }}>
//             <Typography sx={{ fontSize: 12, opacity: 0.85, color: "inherit" }}>
//               Opacity
//             </Typography>
//             <FormControl size="small">
//               <Select
//                 value={overlayOpacity}
//                 onChange={(e) => setOverlayOpacity(Number(e.target.value) as any)}
//                 sx={{
//                   minWidth: 92,
//                   fontSize: 12,
//                   color: "rgba(255,255,255,0.92)",
//                   ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.18)" },
//                   "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.35)" },
//                   ".MuiSvgIcon-root": { color: "rgba(255,255,255,0.7)" },
//                   ".MuiSelect-select": { py: 0.6 },
//                 }}
//                 MenuProps={{
//                   PaperProps: {
//                     sx: { bgcolor: "rgba(20,20,24,0.98)", color: "rgba(255,255,255,0.92)" },
//                   },
//                 }}
//               >
//                 {OPACITY_CHOICES.map((v) => (
//                   <MenuItem key={v} value={v} sx={{ fontSize: 12 }}>
//                     {Math.round(v * 100)}%
//                   </MenuItem>
//                 ))}
//               </Select>
//             </FormControl>
//           </Box>
//         )}

//         <Box sx={{ flex: 1 }} />

//         <IconButton
//           size="small"
//           onClick={onClose}
//           sx={{ color: "rgba(255,255,255,0.85)" }}
//         >
//           <CloseIcon fontSize="small" />
//         </IconButton>
//       </Box>

// <DialogContent
//   sx={{
//     p: 0.75,
//     flex: 1,
//     minHeight: 0,
//     overflow: "hidden",
//     display: "flex",
//     flexDirection: "column",
//   }}
// >
//   {mode === "overlay" ? (
//     <OverlayAllVideos segs={segs} opacity={overlayOpacity} />
//   ) : (
//     <Box
//       sx={{
//         flex: 1,
//         minHeight: 0,
//         width: "100%",
//         // ✅ no JS sizing needed
//         display: "grid",
//         gap: "2px",
//         // tighten: small min tile width; tweak if you want bigger tiles
//         gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
//         alignContent: "start",
//         justifyContent: "start",
//         // allow scrolling if too many (optional; remove if you truly never want scroll)
//         overflow: "auto",
//         bgcolor: "rgba(0,0,0,0.55)",
//         borderRadius: 1.5,
//         border: "2px solid rgba(255,255,255,0.22)",
//         p: 0.5,
//         boxSizing: "border-box",
//       }}
//     >
//       {segs.map((seg) => (
//         <GridTile key={`${seg.rallyIndex}-${seg.startSec}-${seg.endSec}`} seg={seg} />
//       ))}
//     </Box>
//   )}
// </DialogContent>
//     </Dialog>
//   );
// }
